import { ActivitySpinnerWidget, TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Set } from '~commonInfra/utils/Set';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { IOrderClause } from '../../spacesPlatform/serverApi/types/IOrderClause';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { IUserInfo } from '../../userManagement/users/models/IUserInfo';
import { AddUsersToAdminProfileGrid } from './AddUsersToAdminProfileGrid';
import { textFieldStyles } from './styles/AddUsersToAdminProfileDialogStyles';

export const INITIAL_SLICE_SIZE = 100;
export const SCROLL_SLICE_SIZE = 50;

export namespace AddUsersToAdminProfileDialogTestIds {
  export const searchFieldTestId = 'add-users-to-admin-profile-search';
  export const activitySpinnerTestId = 'add-users-to-admin-profile-spinner';
  export const noResultsTestId = 'add-users-to-admin-profile-no-results';
  export const gridRowCheckboxTestId = 'add-users-to-admin-profile-row-';
  export const gridRowDisplayNameTestId = 'add-users-to-admin-profile-displayname-';
}

export enum AddUsersToAdminProfileDataKeys {
  Select = 'selected',
  DisplayName = 'displayName',
  Username = 'username',
}

export type AddUsersToAdminProfileDialogBodyProps = {
  profileName: string;
  getQuery: (searchText: string, resultOrder: IOrderClause<string>) => ResourceQuery<ISiteUserInfo>;
  initialRowCheckboxState?: _.Dictionary<string>;
};

export type AddUsersToAdminProfileDialogState = {
  selectedUserIds: string[];
  selectedUserSet: Set<IUserInfo>;
};

type PropsForDialog = ReactActionDialogBodyProps<
  AddUsersToAdminProfileDialogBodyProps,
  AddUsersToAdminProfileDialogState
>;

function spinnerChild(): JSX.Element {
  return (
    <div className={'tb-data-grid-empty'}>
      <ActivitySpinnerWidget
        testId={AddUsersToAdminProfileDialogTestIds.activitySpinnerTestId}
        shouldShowUnderlay={false}
      />
    </div>
  );
}

function emptyChild(): JSX.Element {
  return (
    <div className={'tb-data-grid-empty'}>
      <div className={'tb-data-grid-empty-title'} data-tb-test-id={AddUsersToAdminProfileDialogTestIds.noResultsTestId}>
        {Messages.noMatchingResults()}
      </div>
    </div>
  );
}

function errorChild(): JSX.Element {
  return (
    <div className={'tb-data-grid-error'}>
      <div className={'tb-data-grid-error-title'}>
        {Messages.errorUnknown()}
      </div>
    </div>
  );
}

export function AddUsersToAdminProfileDialogBody(props: PropsForDialog): JSX.Element {
  const { getQuery, profileName, updateDialogState, initialRowCheckboxState } = props;

  const defaultSortOrder: IOrderClause = {
    field: AddUsersToAdminProfileDataKeys.DisplayName,
    ascending: true,
  };

  const [columnSortOrder] = React.useState<IOrderClause>(defaultSortOrder);
  const [rowCheckboxesState, setRowCheckboxesState] = React.useState<_.Dictionary<string>>(
    initialRowCheckboxState ?? {}
  );
  const [searchText, setSearchText] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<ISiteUserInfo[]>([]);
  const [isError, setIsError] = React.useState(false);
  const [doneInitialFetching, setDoneInitialFetching] = React.useState(false);
  const [remoteResultCount, setRemoteResultCount] = React.useState(0);
  const [localResultCount, setLocalResultCount] = React.useState(0);
  const [allowSliceFetchLock, setAllowSliceFetchLock] = React.useState(true);

  // Tracks whether the component is still mounted so async fetches can bail out
  // after unmount without updating state on a detached component.
  const activeRef = React.useRef(false);

  const fetchFirstChunkOfUsers = React.useCallback(async () => {
    const result: ListResult<ISiteUserInfo> = await getQuery(searchText, columnSortOrder)
      .getSlice(0, INITIAL_SLICE_SIZE);
    if (activeRef.current) {
      setSearchResults(() => result.items);
      setRemoteResultCount(() => result.total);
      setLocalResultCount(() => result.items.length);
      setDoneInitialFetching(() => true);
    }
  }, [getQuery, searchText, columnSortOrder]);

  // Accepts the current local count as a parameter so the callback does not
  // need to close over `localResultCount` state, keeping its deps stable.
  const fetchNextChunkOfUsers = React.useCallback(async (localCount: number) => {
    const result: ListResult<ISiteUserInfo> = await getQuery(searchText, columnSortOrder)
      .getSlice(localCount, SCROLL_SLICE_SIZE);
    setLocalResultCount(prev => prev + result.items.length);
    setSearchResults(prev => prev.concat(result.items));
    setAllowSliceFetchLock(() => true);
  }, [getQuery, searchText, columnSortOrder]);

  React.useEffect(() => {
    activeRef.current = true;
    setDoneInitialFetching(() => false);
    try {
      fetchFirstChunkOfUsers().catch(() => setIsError(() => true));
    } catch (e) {
      setIsError(() => true);
    }
    return () => { activeRef.current = false; };
  }, [fetchFirstChunkOfUsers]);

  // Called by the grid when the user scrolls near the bottom. Fetches the next
  // page of results only when there are more remote results to load and the
  // previous page fetch has finished (allowSliceFetchLock acts as a guard).
  const askForNextPage = React.useCallback(() => {
    if (remoteResultCount !== localResultCount && allowSliceFetchLock) {
      setAllowSliceFetchLock(() => false);
      fetchNextChunkOfUsers(localResultCount).catch(() => setIsError(() => true));
    }
  }, [allowSliceFetchLock, fetchNextChunkOfUsers, localResultCount, remoteResultCount]);

  function handleRowCheckboxChange(user: ISiteUserInfo): void {
    const newState = { ...rowCheckboxesState };
    if (newState[user.id] === CheckboxState.checked) {
      delete newState[user.id];
    } else {
      newState[user.id] = CheckboxState.checked;
    }
    setRowCheckboxesState(() => newState);

    const selectedIds = Object.keys(newState);
    const selectedSet: Set<IUserInfo> = new Set(item => item.id);
    searchResults.forEach(u => {
      if (newState[u.id] === CheckboxState.checked) {
        selectedSet.add(u as IUserInfo);
      }
    });

    updateDialogState(prev => ({
      ...prev,
      selectedUserIds: selectedIds,
      selectedUserSet: selectedSet,
    }));
  }

  const gridEmptyChild = isError
    ? errorChild()
    : doneInitialFetching
      ? emptyChild()
      : spinnerChild();

  return (
    <>
      <div>
        {Messages.adminProfileUsersDialogDescription({ PROFILE_NAME: profileName })}
      </div>
      <TextFieldWidget
        testId={AddUsersToAdminProfileDialogTestIds.searchFieldTestId}
        placeholder={Messages.searchForAUserOrUsername()}
        kind={'search'}
        label={null}
        className={textFieldStyles}
        style={{ width: '100%' }}
        onChange={(e) => setSearchText(e.target.value)}
        message={'\u200B'}
      />
      <AddUsersToAdminProfileGrid
        userList={!isError && doneInitialFetching ? searchResults : []}
        rowCheckboxState={rowCheckboxesState}
        handleRowCheckboxChange={handleRowCheckboxChange}
        emptyChild={gridEmptyChild}
        currentColumnSortOrder={columnSortOrder}
        handleColumnSortOrderChange={() => { /* sorting not needed for this dialog */ }}
        shouldAskForNextPage={(endIndex) =>
          endIndex + 1 === localResultCount && localResultCount < remoteResultCount
        }
        askForNextPage={askForNextPage}
      />
    </>
  );
}
